/**
 * research-panel.ts — modal overlay for M12 research.
 *
 * Opened by R (toggle) or programmatically; closed by R, Escape, or
 * clicking the backdrop. Shows:
 *   - Active research (tech name + rpAccumulated / researchCost text).
 *   - **v3M23.1** — full tech tree as an SVG DAG. Categories sit on the
 *     Y axis (one row band per category, with vertical sub-stacking
 *     when multiple techs share a tier inside a category); tier sits
 *     on the X axis (Mk 1 leftmost, Mk 6 or deepest-prereq rightmost).
 *     Prerequisite edges drawn as cubic Bézier curves with arrow heads
 *     at the destination. Researched / active / available / locked
 *     visual states preserved from the prior list view; click an
 *     available node to dispatch `setResearchTarget`. Replaces the
 *     pre-v3M23 vertical list which buried prereq structure inside
 *     category-then-level sorting.
 *
 * Tick-rate re-render matches detail-panel: the panel's content div is
 * rebuilt on every `update()` call. Event delegation via `pointerdown`
 * (not `click`) avoids the same mid-press rebuild race noted in
 * detail-panel.ts. Keyboard activation is deferred — a R-key-opens-panel
 * listener exists, but Enter/Space on a focused row would not fire
 * pointerdown; polish pass if/when needed.
 */

import type { Command } from '../game/commands';
import type { Empire, GameState, TechDef } from '../state/types';
import { getAudioEngine } from '../audio';

const SVG_NS = 'http://www.w3.org/2000/svg';

// ---------------------------------------------------------------------------
// v3M23.1 — DAG layout constants
// ---------------------------------------------------------------------------

/** Tech-node footprint in SVG coords (matches the prior list-row width feel). */
const NODE_W = 150;
const NODE_H = 36;
/** Horizontal gap between tier columns. */
const COL_GAP = 32;
/** Vertical gap between rows (within a category band, and between categories). */
const ROW_GAP = 8;
/** Left padding for the category label band. */
const LABEL_W = 132;
/** SVG canvas margin inside the panel. */
const SVG_PAD = 12;

export interface ResearchPanelHandle {
  update(state: GameState): void;
  destroy(): void;
  open(): void;
  close(): void;
  toggle(): void;
  isOpen(): boolean;
}

export interface ResearchPanelOptions {
  queueCommand?: (cmd: Command) => void;
}

const PANEL_WIDTH_PX = 960;

export function mountResearchPanel(
  parent: HTMLElement,
  options: ResearchPanelOptions = {},
): ResearchPanelHandle {
  const backdrop = document.createElement('div');
  Object.assign(backdrop.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'rgba(2, 6, 14, 0.55)',
    display: 'none',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '48px',
    zIndex: '50',
    pointerEvents: 'auto',
  });

  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: `${PANEL_WIDTH_PX}px`,
    maxHeight: 'calc(100vh - 96px)',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(6,10,20,0.95)',
    border: '1px solid rgba(80,110,160,0.5)',
    borderRadius: '4px',
    color: '#d8e4ff',
    fontSize: '12px',
    lineHeight: '1.5',
    fontVariantNumeric: 'tabular-nums',
  });

  const titleBar = document.createElement('div');
  Object.assign(titleBar.style, {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(80,110,160,0.35)',
    background: 'rgba(30,50,90,0.45)',
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '0.04em',
  });
  const title = document.createElement('span');
  title.textContent = 'RESEARCH';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close the Research panel. Hotkey: R or Esc.';
  Object.assign(closeBtn.style, {
    font: 'inherit',
    fontSize: '20px',
    lineHeight: '1',
    background: 'transparent',
    border: 'none',
    color: '#8ea0c8',
    cursor: 'pointer',
    padding: '0 6px',
  });
  closeBtn.addEventListener('pointerdown', () => close());
  titleBar.appendChild(title);
  titleBar.appendChild(closeBtn);

  const content = document.createElement('div');
  Object.assign(content.style, {
    padding: '12px 14px',
    overflow: 'auto',
    flex: '1 1 auto',
  });

  panel.appendChild(titleBar);
  panel.appendChild(content);
  backdrop.appendChild(panel);
  parent.appendChild(backdrop);

  // Click the backdrop (but not the panel itself) closes.
  backdrop.addEventListener('pointerdown', (ev) => {
    if (ev.target === backdrop) close();
  });

  // Panel content click delegation for tech rows. pointerdown so the
  // dispatch fires before any per-tick re-render can replace the row DOM.
  content.addEventListener('pointerdown', (ev) => {
    const target = ev.target as Element | null;
    if (!target) return;
    // SVG <g> nodes carry the data-tech-name attribute as well as HTML rows
    // (legacy). closest() and getAttribute() both handle SVG elements.
    const node = target.closest('[data-tech-name]') as Element | null;
    if (!node || node.hasAttribute('data-disabled')) return;
    const techName = node.getAttribute('data-tech-name');
    const empireIdStr = node.getAttribute('data-empire-id');
    const empireId = empireIdStr === null ? NaN : Number(empireIdStr);
    if (!techName || !Number.isFinite(empireId)) return;
    options.queueCommand?.({
      kind: 'setResearchTarget',
      empireId,
      techName,
    });
  });

  let openState = false;
  let lastRenderedKey = '';

  const open = (): void => {
    if (openState) return;
    openState = true;
    backdrop.style.display = 'flex';
    lastRenderedKey = ''; // force re-render
    getAudioEngine().playCue('ui:panel-open');
  };
  const close = (): void => {
    if (!openState) return;
    openState = false;
    backdrop.style.display = 'none';
    getAudioEngine().playCue('ui:panel-close');
  };
  const toggle = (): void => {
    if (openState) close();
    else open();
  };

  const onKeyDown = (ev: KeyboardEvent): void => {
    // Don't swallow keys while a text input has focus (future-proofing —
    // no text inputs exist yet, but buttons don't fire keydowns on space
    // so this is safe either way).
    const tag = (document.activeElement?.tagName ?? '').toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;
    if (ev.key === 'r' || ev.key === 'R') {
      toggle();
      ev.preventDefault();
    } else if (ev.key === 'Escape' && openState) {
      close();
      ev.preventDefault();
    }
  };
  window.addEventListener('keydown', onKeyDown);

  return {
    update(state: GameState): void {
      if (!openState) return;
      const empire = state.empires.get(state.playerEmpireId);
      if (!empire) return;

      // Re-render key mirrors detail-panel's tick-based invalidation,
      // with researchedTechs size so the panel refreshes the moment a
      // tech completes.
      const key = `${state.tick}|${empire.researchedTechs.size}|${empire.activeResearch?.techName ?? 'none'}`;
      if (key === lastRenderedKey) return;
      lastRenderedKey = key;

      renderContent(content, state, empire);
    },
    destroy(): void {
      window.removeEventListener('keydown', onKeyDown);
      backdrop.remove();
    },
    open,
    close,
    toggle,
    isOpen: () => openState,
  };
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function renderContent(host: HTMLElement, state: GameState, empire: Empire): void {
  host.innerHTML = '';

  // Active research summary.
  host.appendChild(sectionHeader('Active'));
  if (empire.activeResearch === null) {
    host.appendChild(dim('— no active research — click a tech below to start —'));
  } else {
    const tech = state.techs.get(empire.activeResearch.techName);
    const cost = tech?.researchCost ?? 0;
    const active = document.createElement('div');
    active.style.padding = '4px 0';
    active.style.fontWeight = '600';
    active.textContent =
      `${empire.activeResearch.techName} · ${Math.floor(empire.activeResearch.rpAccumulated)}/${cost} RP` +
      (empire.resources.research > 0
        ? `  (pool ${Math.floor(empire.resources.research)} RP)`
        : '');
    host.appendChild(active);
  }

  // Tech tree DAG.
  host.appendChild(sectionHeader('Tech tree'));
  if (state.techs.size === 0) {
    host.appendChild(dim('— no techs loaded —'));
    return;
  }
  const layout = computeDagLayout(state.techs);
  const svg = buildDagSvg(layout, empire);
  host.appendChild(svg);
}

/**
 * Player-facing reason this tech can't be targeted right now. Returns null
 * if the tech is freely available (prereqs met, race matches, not already
 * researched, not already active). Already-researched and already-active
 * cases are handled by the caller via visual state, not this helper.
 */
function disabledReason(tech: TechDef, empire: Empire): string | null {
  if (tech.race !== 'All' && tech.race !== empire.raceId) {
    return `Race-locked to ${tech.race}`;
  }
  const missing = tech.prerequisites.filter((p) => !empire.researchedTechs.has(p));
  if (missing.length > 0) {
    return `Requires: ${missing.join(', ')}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Small shared style bits — duplicated from detail-panel.ts to keep this
// panel self-contained. If a third panel wants these, extract to ui/styles.ts.
// ---------------------------------------------------------------------------

function sectionHeader(label: string): HTMLElement {
  const h = document.createElement('div');
  h.textContent = label;
  h.style.marginTop = '14px';
  h.style.marginBottom = '4px';
  h.style.fontSize = '10px';
  h.style.letterSpacing = '0.08em';
  h.style.color = '#7a8bb5';
  h.style.borderBottom = '1px solid rgba(80,110,160,0.25)';
  h.style.paddingBottom = '2px';
  h.style.textTransform = 'uppercase';
  return h;
}

function dim(text: string): HTMLElement {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.color = '#6e80a8';
  el.style.fontStyle = 'italic';
  el.style.padding = '4px 0';
  return el;
}

// ---------------------------------------------------------------------------
// v3M23.1 — DAG layout
// ---------------------------------------------------------------------------

export interface NodeLayout {
  tech: TechDef;
  x: number;
  y: number;
}

export interface EdgeLayout {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface CategoryBand {
  category: string;
  y: number;
  height: number;
  /** y of the divider line drawn below this band, or null for the last band. */
  divider: number | null;
}

export interface DagLayout {
  nodes: NodeLayout[];
  edges: EdgeLayout[];
  categoryBands: CategoryBand[];
  width: number;
  height: number;
}

/**
 * Position every tech on a category × tier grid.
 *
 * Y axis: one band per category, in load order. When multiple techs share a
 * tier inside a category, they stack vertically and the band height grows to
 * fit. Categories never share rows.
 *
 * X axis: tech.level when defined; otherwise depth = max(prereq depth) + 1
 * (1 for techs with no prereqs). Cycle-guarded so a malformed prereq graph
 * doesn't recurse forever.
 */
export function computeDagLayout(techs: ReadonlyMap<string, TechDef>): DagLayout {
  const nodes: NodeLayout[] = [];
  const edges: EdgeLayout[] = [];
  const categoryBands: CategoryBand[] = [];
  const nodeByName = new Map<string, NodeLayout>();

  if (techs.size === 0) {
    return { nodes, edges, categoryBands, width: 0, height: 0 };
  }

  // Step 1: depth per tech (cached, cycle-guarded).
  const depthMap = new Map<string, number>();
  const visiting = new Set<string>();
  const depthOf = (name: string): number => {
    const cached = depthMap.get(name);
    if (cached !== undefined) return cached;
    if (visiting.has(name)) return 1;
    const tech = techs.get(name);
    if (!tech) return 1;
    visiting.add(name);
    let d: number;
    if (typeof tech.level === 'number') {
      d = tech.level;
    } else if (tech.prerequisites.length === 0) {
      d = 1;
    } else {
      let max = 0;
      for (const p of tech.prerequisites) {
        const pd = depthOf(p);
        if (pd > max) max = pd;
      }
      d = max + 1;
    }
    visiting.delete(name);
    depthMap.set(name, d);
    return d;
  };
  for (const tech of techs.values()) {
    depthOf(tech.techName);
  }

  // Step 2: bucket by category preserving load order.
  const byCategory = new Map<string, TechDef[]>();
  for (const tech of techs.values()) {
    let bucket = byCategory.get(tech.category);
    if (!bucket) {
      bucket = [];
      byCategory.set(tech.category, bucket);
    }
    bucket.push(tech);
  }

  // Step 3: max tier across the whole tree (used for SVG width).
  let maxTier = 1;
  for (const d of depthMap.values()) {
    if (d > maxTier) maxTier = d;
  }

  // Step 4: position nodes per category band.
  let yCursor = SVG_PAD;
  for (const [category, techs] of byCategory) {
    const byTier = new Map<number, TechDef[]>();
    let maxStack = 1;
    for (const tech of techs) {
      const t = depthMap.get(tech.techName) ?? 1;
      let bucket = byTier.get(t);
      if (!bucket) {
        bucket = [];
        byTier.set(t, bucket);
      }
      bucket.push(tech);
      if (bucket.length > maxStack) maxStack = bucket.length;
    }
    const bandHeight = maxStack * NODE_H + (maxStack - 1) * ROW_GAP;

    for (const [tier, techsInTier] of byTier) {
      techsInTier.sort((a, b) => a.techName.localeCompare(b.techName));
      const x = SVG_PAD + LABEL_W + (tier - 1) * (NODE_W + COL_GAP);
      const stackHeight = techsInTier.length * NODE_H + (techsInTier.length - 1) * ROW_GAP;
      const yStart = yCursor + (bandHeight - stackHeight) / 2;
      techsInTier.forEach((tech, i) => {
        const node: NodeLayout = {
          tech,
          x,
          y: yStart + i * (NODE_H + ROW_GAP),
        };
        nodes.push(node);
        nodeByName.set(tech.techName, node);
      });
    }

    categoryBands.push({
      category,
      y: yCursor,
      height: bandHeight,
      divider: yCursor + bandHeight + ROW_GAP,
    });
    yCursor += bandHeight + ROW_GAP * 2;
  }
  if (categoryBands.length > 0) {
    categoryBands[categoryBands.length - 1].divider = null;
  }

  // Step 5: prereq edges. Skip self/missing endpoints.
  for (const tech of techs.values()) {
    const to = nodeByName.get(tech.techName);
    if (!to) continue;
    for (const prereq of tech.prerequisites) {
      const from = nodeByName.get(prereq);
      if (!from || from === to) continue;
      edges.push({
        x1: from.x + NODE_W,
        y1: from.y + NODE_H / 2,
        x2: to.x,
        y2: to.y + NODE_H / 2,
      });
    }
  }

  const width =
    SVG_PAD + LABEL_W + (maxTier - 1) * (NODE_W + COL_GAP) + NODE_W + SVG_PAD;
  const height = yCursor + SVG_PAD;

  return { nodes, edges, categoryBands, width, height };
}

/**
 * Render a computed DAG layout to an `<svg>` element. Edges drawn first
 * so node rectangles paint over them. Each node is a `<g data-tech-name>`
 * group; the panel-level pointerdown delegation handles the click.
 */
function buildDagSvg(layout: DagLayout, empire: Empire): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
  svg.setAttribute('width', String(layout.width));
  svg.setAttribute('height', String(layout.height));
  svg.setAttribute('viewBox', `0 0 ${layout.width} ${layout.height}`);
  svg.style.display = 'block';

  // Arrow-head marker for prereq edges.
  const defs = document.createElementNS(SVG_NS, 'defs');
  const marker = document.createElementNS(SVG_NS, 'marker');
  marker.setAttribute('id', 'tech-arrow');
  marker.setAttribute('viewBox', '0 0 10 10');
  marker.setAttribute('refX', '10');
  marker.setAttribute('refY', '5');
  marker.setAttribute('markerWidth', '6');
  marker.setAttribute('markerHeight', '6');
  marker.setAttribute('orient', 'auto-start-reverse');
  const arrowPath = document.createElementNS(SVG_NS, 'path');
  arrowPath.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z');
  arrowPath.setAttribute('fill', 'rgba(140, 170, 220, 0.6)');
  marker.appendChild(arrowPath);
  defs.appendChild(marker);
  svg.appendChild(defs);

  // Category labels and dividers.
  for (const band of layout.categoryBands) {
    const label = document.createElementNS(SVG_NS, 'text');
    label.setAttribute('x', String(SVG_PAD + 4));
    label.setAttribute('y', String(band.y + band.height / 2));
    label.setAttribute('dominant-baseline', 'middle');
    label.setAttribute('text-anchor', 'start');
    label.setAttribute('fill', '#7a8bb5');
    label.setAttribute('font-size', '10');
    label.setAttribute('letter-spacing', '0.08em');
    label.setAttribute('font-weight', '600');
    label.textContent = band.category.toUpperCase();
    svg.appendChild(label);

    if (band.divider !== null) {
      const div = document.createElementNS(SVG_NS, 'line');
      div.setAttribute('x1', String(SVG_PAD));
      div.setAttribute('x2', String(layout.width - SVG_PAD));
      div.setAttribute('y1', String(band.divider));
      div.setAttribute('y2', String(band.divider));
      div.setAttribute('stroke', 'rgba(80,110,160,0.25)');
      div.setAttribute('stroke-width', '1');
      svg.appendChild(div);
    }
  }

  // Edges (drawn before nodes so nodes overpaint endpoints cleanly).
  for (const edge of layout.edges) {
    const path = document.createElementNS(SVG_NS, 'path');
    const dx = Math.max((edge.x2 - edge.x1) * 0.5, 16);
    path.setAttribute(
      'd',
      `M ${edge.x1} ${edge.y1} ` +
        `C ${edge.x1 + dx} ${edge.y1}, ${edge.x2 - dx} ${edge.y2}, ${edge.x2} ${edge.y2}`,
    );
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'rgba(140, 170, 220, 0.45)');
    path.setAttribute('stroke-width', '1.2');
    path.setAttribute('marker-end', 'url(#tech-arrow)');
    svg.appendChild(path);
  }

  // Nodes.
  for (const node of layout.nodes) {
    svg.appendChild(buildDagNode(node, empire));
  }

  return svg;
}

/**
 * One tech node. Visual states mirror the prior list view (researched /
 * active / available / locked) and `data-tech-name` + `data-empire-id`
 * carry the click target through the panel-level delegation.
 */
function buildDagNode(node: NodeLayout, empire: Empire): SVGGElement {
  const { tech } = node;
  const isResearched = empire.researchedTechs.has(tech.techName);
  const isActive = empire.activeResearch?.techName === tech.techName;
  const reason = disabledReason(tech, empire);

  const g = document.createElementNS(SVG_NS, 'g') as SVGGElement;
  g.setAttribute('transform', `translate(${node.x}, ${node.y})`);
  g.setAttribute('data-tech-name', tech.techName);
  g.setAttribute('data-empire-id', String(empire.id));

  let fill = 'rgba(40, 60, 100, 0.45)';
  let stroke = 'rgba(80,110,160,0.55)';
  let strokeWidth = '1';
  let textColor = '#d8e4ff';
  let opacity = '1';
  let cursor = 'pointer';

  if (isResearched) {
    fill = 'rgba(40, 70, 50, 0.35)';
    stroke = 'rgba(120, 170, 130, 0.55)';
    textColor = '#bcd4be';
    opacity = '0.75';
    cursor = 'default';
  } else if (isActive) {
    fill = 'rgba(80, 140, 220, 0.35)';
    stroke = 'rgba(140, 190, 255, 0.85)';
    strokeWidth = '1.5';
    cursor = 'default';
  } else if (reason !== null) {
    fill = 'rgba(40, 60, 100, 0.25)';
    stroke = 'rgba(80,110,160,0.3)';
    opacity = '0.5';
    cursor = 'not-allowed';
    g.setAttribute('data-disabled', 'true');
  }

  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('width', String(NODE_W));
  rect.setAttribute('height', String(NODE_H));
  rect.setAttribute('rx', '3');
  rect.setAttribute('ry', '3');
  rect.setAttribute('fill', fill);
  rect.setAttribute('stroke', stroke);
  rect.setAttribute('stroke-width', strokeWidth);
  rect.setAttribute('opacity', opacity);
  g.appendChild(rect);

  // v4M1.11 — hover lift on available tech nodes. Researched / active /
  // disabled nodes don't respond to hover so the cursor: pointer state
  // already telegraphs whether the node is clickable. SVG attribute
  // mutations are batched by the browser; no transition needed.
  if (!isResearched && !isActive && reason === null) {
    const baseFill = fill;
    const hoverFill = 'rgba(80, 130, 200, 0.55)';
    const baseStroke = stroke;
    const hoverStroke = 'rgba(180, 220, 255, 0.85)';
    g.addEventListener('mouseenter', () => {
      rect.setAttribute('fill', hoverFill);
      rect.setAttribute('stroke', hoverStroke);
    });
    g.addEventListener('mouseleave', () => {
      rect.setAttribute('fill', baseFill);
      rect.setAttribute('stroke', baseStroke);
    });
  }

  const nameText = document.createElementNS(SVG_NS, 'text');
  nameText.setAttribute('x', '8');
  nameText.setAttribute('y', '15');
  nameText.setAttribute('fill', textColor);
  nameText.setAttribute('font-size', '11');
  nameText.setAttribute('font-weight', '600');
  nameText.setAttribute('opacity', opacity);
  nameText.textContent = (isResearched ? '✓ ' : '') + truncateText(tech.techName, 22);
  g.appendChild(nameText);

  const costText = document.createElementNS(SVG_NS, 'text');
  costText.setAttribute('x', '8');
  costText.setAttribute('y', '29');
  costText.setAttribute('fill', '#8ea0c8');
  costText.setAttribute('font-size', '10');
  costText.setAttribute('opacity', opacity);
  costText.textContent = tech.researchCost === 0 ? 'free' : `${tech.researchCost} RP`;
  g.appendChild(costText);

  g.style.cursor = cursor;

  // v4M1.2 — every node gets a hover tooltip. State-dependent text:
  //   researched → "Already researched"
  //   active     → "Currently researching: <accumulated> / <cost> RP"
  //   disabled   → existing prereq-or-race reason
  //   available  → click-prompt with cost + category
  const titleEl = document.createElementNS(SVG_NS, 'title');
  if (isResearched) {
    titleEl.textContent = `${tech.techName} — researched. ${tech.category} category.`;
  } else if (isActive) {
    const acc = Math.floor(empire.activeResearch?.rpAccumulated ?? 0);
    titleEl.textContent =
      `${tech.techName} — researching. ${acc} / ${tech.researchCost} RP. ${tech.category} category.`;
  } else if (reason !== null) {
    titleEl.textContent = `${tech.techName} — ${reason}`;
  } else {
    titleEl.textContent =
      `${tech.techName} — click to start research. Cost ${tech.researchCost} RP. ${tech.category} category.`;
  }
  g.appendChild(titleEl);

  return g;
}

function truncateText(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
